import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus, Eye, Trash2, Award, Clock, Users, ChevronRight, CheckCircle, XCircle, Edit2, Filter, TrendingUp, BarChart2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { toast } from 'sonner';
import api from '../../api';

const statusColor = { active: 'bg-emerald-50 text-emerald-700', closed: 'bg-slate-100 text-slate-600' };
const submissionBadge = (s) => s === 'graded' ? 'bg-emerald-50 text-emerald-700' : s === 'late' ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700';

export default function TeacherAssignmentsPage() {
  const [assignments, setAssignments] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [viewAsgn, setViewAsgn] = useState(null);
  const [myAssignments, setMyAssignments] = useState([]);
  const [filterStatus, setFilterStatus] = useState('');
  const [page, setPage] = useState(1);

  const fetchAssignments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/assignments?page=${page}&limit=20${filterStatus ? `&status=${filterStatus}` : ''}`);
      setAssignments(res.data || []);
      setTotal(res.total || 0);
    } catch { toast.error('Failed to load assignments'); }
    finally { setLoading(false); }
  }, [page, filterStatus]);

  useEffect(() => { fetchAssignments(); }, [fetchAssignments]);
  useEffect(() => {
    api.get('/academics/my-assignments').then(r => setMyAssignments(r.data || [])).catch(() => {});
  }, []);

  const deleteAssignment = async (id) => {
    if (!window.confirm('Delete this assignment? All submissions will be lost.')) return;
    try {
      await api.delete(`/assignments/${id}`);
      toast.success('Assignment deleted');
      fetchAssignments();
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6 animate-fade-in" data-testid="teacher-assignments-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-outfit text-2xl font-bold text-slate-900">Assignments</h1>
          <p className="text-slate-500 text-sm mt-0.5">{total} total • Create and grade class assignments</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-[#516EFA] hover:bg-[#4058e8] text-white" data-testid="create-assignment-btn">
          <Plus className="w-4 h-4 mr-2" />Create Assignment
        </Button>
      </div>

      {/* Filter */}
      <div className="flex gap-3">
        <Select value={filterStatus || 'all'} onValueChange={v => setFilterStatus(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-36 h-9"><SelectValue placeholder="All status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Assignment List */}
      {loading ? (
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => <div key={i} className="h-20 bg-slate-100 rounded-xl animate-pulse" />)}
        </div>
      ) : assignments.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-100 py-16 text-center">
          <div className="w-12 h-12 rounded-xl bg-[#eef1ff] flex items-center justify-center mx-auto mb-3">
            <Award className="w-6 h-6 text-[#516EFA]" />
          </div>
          <p className="text-slate-800 font-semibold">No assignments yet</p>
          <p className="text-slate-500 text-sm mt-1">Create your first assignment for students</p>
          <Button onClick={() => setShowCreate(true)} className="mt-4 bg-[#516EFA] text-white">Create Assignment</Button>
        </div>
      ) : (
        <div className="space-y-3">
          {assignments.map(a => (
            <div key={a.id} className="bg-white rounded-xl border border-slate-100 p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:shadow-sm transition-shadow">
              <div className="flex items-start gap-4 flex-1">
                <div className="w-10 h-10 rounded-xl bg-[#eef1ff] flex items-center justify-center flex-shrink-0">
                  <Award className="w-5 h-5 text-[#516EFA]" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-slate-900 text-sm">{a.title}</p>
                    <Badge className={`text-xs capitalize ${statusColor[a.status] || 'bg-slate-50 text-slate-600'}`}>{a.status}</Badge>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">{a.subject_name} • {(a.class_names || []).join(', ')}</p>
                  <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-500">
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />Due: {a.due_date || 'N/A'}</span>
                    <span className="flex items-center gap-1"><Users className="w-3 h-3" />{a.submission_count || 0} submitted</span>
                    <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-500" />{a.graded_count || 0} graded</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setViewAsgn(a)}>
                  <Eye className="w-3.5 h-3.5 mr-1.5" />View
                </Button>
                <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-400 hover:text-red-600 hover:bg-red-50" onClick={() => deleteAssignment(a.id)}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Assignment Dialog */}
      {showCreate && (
        <CreateAssignmentDialog
          myAssignments={myAssignments}
          onClose={() => setShowCreate(false)}
          onSuccess={() => { setShowCreate(false); fetchAssignments(); }}
        />
      )}

      {/* View/Grade Submissions Dialog */}
      {viewAsgn && (
        <SubmissionsDialog
          assignment={viewAsgn}
          onClose={() => setViewAsgn(null)}
          onGraded={fetchAssignments}
        />
      )}
    </div>
  );
}

function CreateAssignmentDialog({ myAssignments, onClose, onSuccess }) {
  // Unique subjects from teacher's assignments
  const allSubjects = [...new Set((myAssignments || []).map(a => a.subject_name).filter(Boolean))].sort();

  const [form, setForm] = useState({ title: '', description: '', subject_name: '', class_ids: [], class_names: [], due_date: '', max_score: 100, instructions: '' });
  const [saving, setSaving] = useState(false);

  // Classes where teacher teaches the selected subject
  const classesForSubject = form.subject_name
    ? (myAssignments || []).filter(a => a.subject_name === form.subject_name).map(a => ({ id: a.class_id, name: a.class_name }))
    : [];

  const handleSubjectChange = (subject) => {
    setForm(p => ({ ...p, subject_name: subject, class_ids: [], class_names: [] }));
  };

  const toggleClass = (cls) => {
    setForm(p => {
      const has = p.class_ids.includes(cls.id);
      return {
        ...p,
        class_ids: has ? p.class_ids.filter(id => id !== cls.id) : [...p.class_ids, cls.id],
        class_names: has ? p.class_names.filter(n => n !== cls.name) : [...p.class_names, cls.name],
      };
    });
  };

  const handleSave = async () => {
    if (!form.title || !form.subject_name || form.class_ids.length === 0) { toast.error('Title, subject and at least one class are required'); return; }
    setSaving(true);
    try {
      await api.post('/assignments', form);
      toast.success('Assignment created successfully');
      onSuccess();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to create'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-outfit">Create Assignment</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs text-slate-600 mb-1 block">Title *</Label>
            <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="e.g. Mathematics Chapter 3 Exercise" className="h-9" data-testid="assignment-title-input" />
          </div>
          <div>
            <Label className="text-xs text-slate-600 mb-1 block">Subject *</Label>
            {allSubjects.length > 0 ? (
              <Select value={form.subject_name} onValueChange={handleSubjectChange}>
                <SelectTrigger className="h-9" data-testid="assignment-subject-select">
                  <SelectValue placeholder="Select your subject" />
                </SelectTrigger>
                <SelectContent>
                  {allSubjects.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <div className="h-9 flex items-center px-3 rounded-lg border border-slate-200 bg-slate-50 text-xs text-slate-400">
                No subjects assigned yet — contact your admin
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs text-slate-600 mb-1 block">Description</Label>
            <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Briefly describe the assignment..." rows={2} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#516EFA]/20 resize-none" />
          </div>
          <div>
            <Label className="text-xs text-slate-600 mb-1 block">Instructions</Label>
            <textarea value={form.instructions} onChange={e => setForm(p => ({ ...p, instructions: e.target.value }))} placeholder="Detailed instructions for students..." rows={3} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#516EFA]/20 resize-none" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-600 mb-1 block">Due Date</Label>
              <Input type="date" value={form.due_date} onChange={e => setForm(p => ({ ...p, due_date: e.target.value }))} className="h-9" />
            </div>
            <div>
              <Label className="text-xs text-slate-600 mb-1 block">Max Score</Label>
              <Input type="number" value={form.max_score} onChange={e => setForm(p => ({ ...p, max_score: Number(e.target.value) }))} className="h-9" min={1} max={200} />
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-600 mb-1.5 block">
              Assign to Classes *
              {!form.subject_name && <span className="text-slate-400 ml-1">(select a subject first)</span>}
            </Label>
            {classesForSubject.length > 0 ? (
              <div className="grid grid-cols-2 gap-2">
                {classesForSubject.map(cls => (
                  <button key={cls.id} onClick={() => toggleClass(cls)}
                    data-testid={`class-toggle-${cls.id}`}
                    className={`flex items-center gap-2 p-2.5 rounded-lg border text-xs text-left transition-all ${form.class_ids.includes(cls.id) ? 'bg-[#eef1ff] border-[#516EFA] text-[#516EFA]' : 'bg-slate-50 border-slate-200 text-slate-700 hover:border-[#516EFA]/40'}`}>
                    {form.class_ids.includes(cls.id) ? <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" /> : <div className="w-3.5 h-3.5 rounded-full border border-slate-300 flex-shrink-0" />}
                    {cls.name}
                  </button>
                ))}
              </div>
            ) : (
              <div className="py-4 text-center text-xs text-slate-400 border border-dashed border-slate-200 rounded-lg">
                {form.subject_name ? 'No classes assigned for this subject' : 'Select a subject to see your assigned classes'}
              </div>
            )}
          </div>
          <div className="flex gap-3 pt-2">
            <Button onClick={handleSave} disabled={saving} className="flex-1 bg-[#516EFA] hover:bg-[#4058e8] text-white" data-testid="save-assignment-btn">
              {saving ? 'Creating...' : 'Create Assignment'}
            </Button>
            <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SubmissionsDialog({ assignment, onClose, onGraded }) {
  const [submissions, setSubmissions] = useState([]);
  const [nonSubmitted, setNonSubmitted] = useState([]);
  const [loading, setLoading] = useState(true);
  const [grading, setGrading] = useState(null);
  const [gradeForm, setGradeForm] = useState({ score: '', feedback: '' });
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('submitted');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/assignments/${assignment.id}/submissions`);
      setSubmissions(r.data || []);
      setNonSubmitted(r.non_submitted || []);
    } catch { toast.error('Failed to load submissions'); }
    finally { setLoading(false); }
  }, [assignment.id]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleGrade = async (sub) => {
    if (gradeForm.score === '') { toast.error('Enter a score'); return; }
    const score = Number(gradeForm.score);
    if (score < 0 || score > assignment.max_score) { toast.error(`Score must be 0–${assignment.max_score}`); return; }
    setSaving(true);
    try {
      await api.put(`/assignments/${assignment.id}/submissions/${sub.id}/grade`, { score, feedback: gradeForm.feedback });
      toast.success('Submission graded');
      setGrading(null);
      setGradeForm({ score: '', feedback: '' });
      await loadData();
      onGraded();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to grade'); }
    finally { setSaving(false); }
  };

  const gradedCount = submissions.filter(s => s.status === 'graded').length;

  // Score distribution for Analytics tab
  const distribution = useMemo(() => {
    const graded = submissions.filter(s => s.score !== null && s.score !== undefined);
    if (!graded.length) return null;
    const RANGES = [
      { range: '0–20%',  min: 0,  max: 20,  fill: '#EF4444', count: 0 },
      { range: '21–40%', min: 21, max: 40,  fill: '#F97316', count: 0 },
      { range: '41–60%', min: 41, max: 60,  fill: '#F59E0B', count: 0 },
      { range: '61–80%', min: 61, max: 80,  fill: '#2563EB', count: 0 },
      { range: '81–100%',min: 81, max: 100, fill: '#10B981', count: 0 },
    ];
    for (const s of graded) {
      const pct = Math.min(100, Math.round((s.score / assignment.max_score) * 100));
      for (const r of RANGES) { if (pct >= r.min && pct <= r.max) { r.count++; break; } }
    }
    const scores = graded.map(s => s.score);
    return {
      ranges: RANGES,
      avg:    Math.round(scores.reduce((a, b) => a + b, 0) / scores.length),
      avgPct: Math.round((scores.reduce((a, b) => a + b, 0) / scores.length / assignment.max_score) * 100),
      high:   Math.max(...scores),
      low:    Math.min(...scores),
      total:  graded.length,
    };
  }, [submissions, assignment.max_score]);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-outfit">{assignment.title}</DialogTitle>
          <p className="text-xs text-slate-500">{assignment.subject_name} · Due: {assignment.due_date || 'N/A'} · Max: {assignment.max_score} pts</p>
        </DialogHeader>

        {/* Summary bar */}
        <div className="grid grid-cols-3 gap-3 mt-1 mb-3">
          {[
            { label: 'Submitted', value: submissions.length, color: '#2563EB' },
            { label: 'Graded',    value: gradedCount,         color: '#10B981' },
            { label: 'Pending',   value: nonSubmitted.length, color: '#F59E0B' },
          ].map(m => (
            <div key={m.label} className="bg-slate-50 rounded-xl p-3 text-center border border-slate-100">
              <p className="text-xl font-bold" style={{ color: m.color }}>{m.value}</p>
              <p className="text-xs text-slate-500 mt-0.5">{m.label}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit mb-4">
          <button
            onClick={() => setTab('submitted')}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${tab === 'submitted' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid="tab-submitted"
          >
            Submitted ({submissions.length})
          </button>
          <button
            onClick={() => setTab('pending')}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${tab === 'pending' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid="tab-not-submitted"
          >
            Not Submitted ({nonSubmitted.length})
          </button>
          {distribution && (
            <button
              onClick={() => setTab('analytics')}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all flex items-center gap-1 ${tab === 'analytics' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              data-testid="tab-analytics"
            >
              <BarChart2 className="w-3 h-3" />Analytics
            </button>
          )}
        </div>

        {loading ? (
          <div className="space-y-2 py-4">{[...Array(3)].map((_, i) => <div key={i} className="h-16 bg-slate-100 rounded-lg animate-pulse" />)}</div>
        ) : tab === 'submitted' ? (
          submissions.length === 0 ? (
            <div className="py-12 text-center">
              <Users className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              <p className="text-sm text-slate-500">No submissions yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {submissions.map(sub => (
                <div key={sub.id} className="border border-slate-100 rounded-xl p-4" data-testid={`submission-row-${sub.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-[10px] flex-shrink-0">
                          {sub.student_name?.[0]?.toUpperCase() || '?'}
                        </div>
                        <p className="font-medium text-slate-900 text-sm">{sub.student_name}</p>
                        <Badge className={`text-xs capitalize ${submissionBadge(sub.status)}`}>{sub.status}</Badge>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">{sub.class_name || 'N/A'} · {sub.admission_number || ''}</p>
                      {sub.content && (
                        <p className="text-xs text-slate-600 mt-2 bg-slate-50 p-2 rounded-lg line-clamp-2">{sub.content}</p>
                      )}
                      {sub.score !== null && sub.score !== undefined && (
                        <div className="mt-2 flex items-center gap-2 flex-wrap">
                          <Badge className="bg-[#eef1ff] text-[#516EFA] font-bold">{sub.score}/{assignment.max_score}</Badge>
                          {sub.feedback && <p className="text-xs text-slate-500 italic">"{sub.feedback}"</p>}
                        </div>
                      )}
                    </div>
                    <Button size="sm" variant="outline" className="h-7 text-xs flex-shrink-0"
                      onClick={() => { setGrading(sub); setGradeForm({ score: sub.score ?? '', feedback: sub.feedback ?? '' }); }}
                      data-testid={`grade-btn-${sub.id}`}>
                      <Award className="w-3 h-3 mr-1" />{sub.status === 'graded' ? 'Re-grade' : 'Grade'}
                    </Button>
                  </div>

                  {grading?.id === sub.id && (
                    <div className="mt-3 pt-3 border-t border-slate-100 flex gap-3 items-end flex-wrap">
                      <div className="flex-1 min-w-[100px]">
                        <Label className="text-xs text-slate-600 mb-1 block">Score (0 – {assignment.max_score})</Label>
                        <Input type="number" value={gradeForm.score} onChange={e => setGradeForm(p => ({ ...p, score: e.target.value }))}
                          min={0} max={assignment.max_score} className="h-8 text-sm" placeholder="75" />
                      </div>
                      <div className="flex-[2] min-w-[140px]">
                        <Label className="text-xs text-slate-600 mb-1 block">Feedback</Label>
                        <Input value={gradeForm.feedback} onChange={e => setGradeForm(p => ({ ...p, feedback: e.target.value }))}
                          className="h-8 text-sm" placeholder="Optional comment..." />
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <Button size="sm" onClick={() => handleGrade(sub)} disabled={saving}
                          className="bg-[#516EFA] text-white h-8 text-xs px-3" data-testid="save-grade-btn">
                          {saving ? '...' : 'Save'}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setGrading(null)} className="h-8 text-xs">Cancel</Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )
        ) : tab === 'pending' ? (
          /* NOT SUBMITTED tab */
          nonSubmitted.length === 0 ? (
            <div className="py-12 text-center">
              <CheckCircle className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
              <p className="text-sm text-slate-500 font-semibold">All students have submitted!</p>
            </div>
          ) : (
            <div className="space-y-2">
              {nonSubmitted.map((s) => (
                <div key={s.student_id} className="flex items-center gap-3 border border-slate-100 rounded-xl p-3 bg-amber-50/40"
                  data-testid={`non-submitted-${s.student_id}`}>
                  <div className="w-7 h-7 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 font-bold text-[10px] flex-shrink-0">
                    {s.student_name?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{s.student_name}</p>
                    <p className="text-xs text-slate-400">{s.class_name} · {s.admission_number || 'N/A'}</p>
                  </div>
                  <span className="text-xs font-semibold text-amber-600 bg-amber-100 px-2 py-1 rounded-lg flex-shrink-0">Not submitted</span>
                </div>
              ))}
            </div>
          )
        ) : tab === 'analytics' && distribution ? (
          /* ── ANALYTICS TAB ─────────────────────────────────────── */
          <div data-testid="analytics-tab">
            {/* Stats row */}
            <div className="grid grid-cols-3 gap-3 mb-5">
              {[
                { label: 'Average',   value: `${distribution.avg}`, sub: `${distribution.avgPct}%`, color: '#2563EB' },
                { label: 'Highest',   value: `${distribution.high}`, sub: `of ${assignment.max_score}`, color: '#10B981' },
                { label: 'Lowest',    value: `${distribution.low}`,  sub: `of ${assignment.max_score}`, color: '#EF4444' },
              ].map(m => (
                <div key={m.label} className="bg-slate-50 rounded-xl p-3 text-center border border-slate-100">
                  <p className="text-xl font-bold" style={{ color: m.color }}>{m.value}</p>
                  <p className="text-[10px] text-slate-400">{m.sub}</p>
                  <p className="text-xs text-slate-500 font-medium mt-0.5">{m.label}</p>
                </div>
              ))}
            </div>

            {/* Distribution chart */}
            <div className="bg-white rounded-xl border border-slate-100 p-4" data-testid="score-distribution-chart">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4">
                Score Distribution · {distribution.total} graded
              </p>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={distribution.ranges} barSize={36} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                  <XAxis dataKey="range" tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                  <Tooltip
                    content={({ active, payload, label }) =>
                      active && payload?.length ? (
                        <div className="px-3 py-2 rounded-xl text-sm shadow-xl" style={{ backgroundColor: '#0F172A' }}>
                          <p className="text-slate-400 text-xs mb-0.5">{label}</p>
                          <p className="font-bold text-white">{payload[0].value} student{payload[0].value !== 1 ? 's' : ''}</p>
                        </div>
                      ) : null
                    }
                    cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                  />
                  <Bar dataKey="count" radius={[5, 5, 0, 0]}>
                    {distribution.ranges.map((r, i) => (
                      <Cell key={i} fill={r.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
